import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { auth } from '../firebase';
import {
  GoogleAuthProvider,
  signInWithPopup,
  RecaptchaVerifier,
  signInWithPhoneNumber,
  OAuthProvider
} from 'firebase/auth';
import PhoneInput from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import OtpInput from 'react-otp-input';
import { FaGoogle, FaApple } from 'react-icons/fa';
import '../styles/Login.css';
import { trackEvent } from '../analytics';

function Login({ setLanguage }) {
  const { t, i18n } = useTranslation();
  const [phoneNumber, setPhoneNumber] = useState('');
  const [country, setCountry] = useState('GB');
  const [confirmationResult, setConfirmationResult] = useState(null);
  const [otp, setOtp] = useState('');
  const [resendCooldown, setResendCooldown] = useState(0);
  const [error, setError] = useState('');

  useEffect(() => {
    setupRecaptcha();

    const detectedLng = i18n.language;
    let countryCode = 'GB'; // Default
    if (detectedLng.startsWith('pl')) {
      countryCode = 'PL';
    } else if (detectedLng.startsWith('fr')) {
      countryCode = 'FR';
    } else if (detectedLng.startsWith('es')) {
      countryCode = 'ES';
    } else if (detectedLng.startsWith('zh')) {
      countryCode = 'CN';
    } else if (detectedLng.startsWith('uk')) {
      countryCode = 'UA';
    }
    setCountry(countryCode);
  }, [i18n.language]);

  useEffect(() => {
    let timer;
    if (resendCooldown > 0) {
      timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
    }
    return () => clearTimeout(timer);
  }, [resendCooldown]);

  const setupRecaptcha = () => {
    if (!window.recaptchaVerifier) {
      window.recaptchaVerifier = new RecaptchaVerifier(auth, 'recaptcha-container', {
        'size': 'invisible',
        'callback': (response) => {
          console.log("Recaptcha verified");
        }
      });
    }
  };

  const signInWithGoogle = async () => {
    const provider = new GoogleAuthProvider();
    try {
      await signInWithPopup(auth, provider);
      trackEvent('login', { method: 'google' });
    } catch (error) {
      console.error('Google sign-in error:', error);
    }
  };

  const signInWithApple = async () => {
    const provider = new OAuthProvider('apple.com');
    try {
      await signInWithPopup(auth, provider);
      trackEvent('login', { method: 'apple' });
    } catch (error) {
      console.error('Apple sign-in error:', error);
    }
  };

  const handlePhoneSignIn = async () => {
    try {
      if(!window.recaptchaVerifier){
        setupRecaptcha();
      }
      setError('');
      const confirmation = await signInWithPhoneNumber(auth, phoneNumber, window.recaptchaVerifier);
      setConfirmationResult(confirmation);
      setResendCooldown(45);
    } catch (error) {
      console.error("Error sending OTP:", error);
      setError('Invalid phone number. Please try again.');
      if (error.code === 'auth/invalid-verification-code') {
        setupRecaptcha();
      }
    }
  };

  const handleOtpSubmit = async () => {
    try {
      await confirmationResult.confirm(otp);
      trackEvent('login', { method: 'phone' });
      setError('');
    } catch (error) {
      console.error("Error verifying OTP:", error);
      setError('Invalid verification code. Please try again.');
    }
  };

  const handleResendOtp = async () => {
    if (resendCooldown === 0) {
      await handlePhoneSignIn();
    }
  };

  const handleCountryChange = (selectedCountry) => {
    setCountry(selectedCountry);
    let lang = 'en';
    if (selectedCountry === 'PL') {
      lang = 'pl';
    } else if (selectedCountry === 'FR') {
      lang = 'fr';
    } else if (selectedCountry === 'ES') {
      lang = 'es';
    } else if (selectedCountry === 'CN') {
      lang = 'zh';
    } else if (selectedCountry === 'UA') {
      lang = 'uk';
    }
    i18n.changeLanguage(lang);
    setLanguage(lang);
  };

  return (
    <div className="login-container">
      <div className="top-section">
        <img src="icons/icon-512.png" alt="logo" className="logo" />
        <h1>{t('login.welcome')}</h1>
        <p>{t('login.discover')}</p>
      </div>
      <div className="bottom-section">
        {!confirmationResult ? (
          <div>
            <h1>{t('login.get_started')}</h1>
            <p>{t('login.enter_phone')}</p>
            <div className="phone-input-container">
              <PhoneInput
                placeholder={t('login.phone_placeholder')}
                value={phoneNumber}
                onChange={setPhoneNumber}
                country={country}
                onCountryChange={handleCountryChange}
                countries={['GB', 'PL', 'FR', 'ES', 'CN', 'UA']}
              />
            </div>
            <button onClick={handlePhoneSignIn} className="send-code-button">{t('login.send_code')}</button>
            {error && <p className="error-message">{error}</p>}
            <div className="or-login-with">{t('login.or_login_with')}</div>
            <div className="social-login">
              <button onClick={signInWithGoogle} className="social-button">
                <FaGoogle /> Google
              </button>
              <button onClick={signInWithApple} className="social-button">
                <FaApple /> Apple
              </button>
            </div>
          </div>
        ) : (
          <div>
            <h1>{t('login.check_messages')}</h1>
            <p>{t('login.sent_code', { phoneNumber })}</p>
            <div className="otp-input-container">
              <OtpInput
                value={otp}
                onChange={setOtp}
                numInputs={6}
                renderInput={(props) => <input {...props} />}
                inputStyle="otp-input"
                inputType="number"
              />
            </div>
            <div className="resend-code">
              {t('login.didnt_get_code')}{' '}
              {resendCooldown > 0 ? (
                t('login.resend_in', { count: resendCooldown })
              ) : (
                <button onClick={handleResendOtp} className="link-button">
                  {t('login.resend')}
                </button>
              )}
            </div>
            <button onClick={handleOtpSubmit} className="verify-button">{t('login.verify')}</button>
            {error && <p className="error-message">{error}</p>}
          </div>
        )}
      </div>
    </div>
  );
}

export default Login;
